import {
  Button,
  Card,
  Col,
  DeleteButton,
  EditButton,
  Form,
  getDefaultSortOrder,
  Icons,
  List,
  Row,
  Select,
  ShowButton,
  Space,
  Table,
  TagField,
  TextField,
  useSelect,
  useTable,
} from "@pankod/refine-antd";
import {
  HttpError,
  IResourceComponentsProps,
  LogicalFilter
} from "@pankod/refine-core";
import { ICampaign, IHealthInsurer } from "interfaces";
import { ICampaignFilter } from "interfaces/campaign";
import { useInfiniteSelect } from "hooks/useInfiniteSelect";
import { useMemo } from "react";

export const CampaignList: React.FC<IResourceComponentsProps> = () => {
  const { tableProps, sorter, searchFormProps, tableQueryResult } = useTable<
    ICampaign,
    HttpError,
    ICampaignFilter
  >({
    resource: "v1/campaign/list",
    initialCurrent: 1,
    initialPageSize: 20,
    hasPagination: true,
    onSearch: (values) => {
      const filtros: LogicalFilter[] = Object.entries(values).map(
        ([key, value]) => ({
          field: key,
          operator: "eq",
          value,
        })
      );
      return filtros;
    },
  });

  const handleCleanFilters = () => {
    searchFormProps.form?.resetFields();
    searchFormProps.form?.submit();
  };

  const { selectProps: healthInsurerSelectProps } = useSelect<IHealthInsurer>({
    resource: "v1/health-insurer",
    optionLabel: "Description",
    optionValue: "HealthInsurerId",
    hasPagination: true,
  });

  const healthInsurerOptions = useMemo(() => {
    const options = healthInsurerSelectProps.options ?? [];
    const unifiedOption = { value: 9999, label: "Domínio Unificado" };

    const hasUnified = options.some(
      (option) => String(option?.value) === String(unifiedOption.value)
    );

    return hasUnified ? options : [...options, unifiedOption];
  }, [healthInsurerSelectProps.options]);

  const { selectProps: partnerSelectProps } = useInfiniteSelect({
    resource: "v1/partners",
    optionLabel: "partner_name",
    optionValue: "partner_code",
    sort: [{ field: "partner_name", order: "asc" }],
    pageSize: 20,
  });

  return (
    <List
      title="Campanhas"
      createButtonProps={{ children: "Incluir", type: "primary" }}
    >
      <Card title="Busca">
        <Form {...searchFormProps} layout="vertical">
          <Row gutter={[16, 16]} style={{ width: "100%" }}>
            <Col xs={24} sm={12}>
              <Form.Item name="health_insurer_id" label="Operadora">
                <Select
                  {...healthInsurerSelectProps}
                  options={healthInsurerOptions}
                  placeholder="Operadora"
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item name="partner_code" label="Parceiro">
                <Select {...partnerSelectProps} placeholder="Parceiro" />
              </Form.Item>
            </Col>

            <Col xs={24} style={{ textAlign: "right", marginTop: 8 }}>
              <Space>
                <Button
                  type="default"
                  icon={<Icons.ClearOutlined />}
                  onClick={handleCleanFilters}
                >
                  Limpar
                </Button>
                <Button
                  type="primary"
                  icon={<Icons.SearchOutlined />}
                  htmlType="submit"
                >
                  Buscar
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Card>

      <Table {...tableProps} rowKey="_id">
        <Table.Column
          dataIndex="health_insurer_name"
          key="health_insurer_name"
          title="Operadora"
          render={(value) => <TextField value={value} />}
          defaultSortOrder={getDefaultSortOrder("health_insurer_name", sorter)}
          sorter
        />
        <Table.Column
          dataIndex="message"
          key="message"
          title="Mensagem"
          render={(value) => (
            <TextField
              value={value}
              style={{
                wordBreak: "break-all",
                textWrap: "wrap",
                maxWidth: "100%",
              }}
            />
          )}
          defaultSortOrder={getDefaultSortOrder("message", sorter)}
          sorter
        />

        <Table.Column
          dataIndex="partner_name"
          key="partner_name"
          title="Parceiro"
          render={(value) => <TextField value={value || "-"} />}
        />

        <Table.Column
          dataIndex="campaign_id"
          key="campaign_id"
          title="Id da Campanha"
          render={(value) => <TextField value={value} />}
          defaultSortOrder={getDefaultSortOrder("campaign_id", sorter)}
          sorter
        />
        <Table.Column
          dataIndex="tag"
          key="tag"
          title="Tag"
          render={(value) => <TagField value={value} />}
          defaultSortOrder={getDefaultSortOrder("tag", sorter)}
          sorter
        />

        <Table.Column<ICampaign>
          title="Ações"
          dataIndex="actions"
          render={(_, record) => (
            <Space>
              <EditButton
                hideText
                size="small"
                recordItemId={record._id}
              />
              <ShowButton hideText size="small" recordItemId={record._id} />
              <DeleteButton
                hideText
                size="small"
                recordItemId={record._id}
                onSuccess={() => tableQueryResult.refetch()}
              />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
